/**
 * Sprint T-17.1 · Notification Center · OverviewTab.
 * Ejecutivo · 30s auto-refresh · widgets independientes.
 * §4.1 · 8 widgets.
 */
import React, { useMemo } from "react";
import { AlertOctagon, Bell, ShieldCheck, Info, Gauge } from "lucide-react";
import { WidgetCard, WidgetGrid, MetricCard, EmptyState, LoadingState, CitationBadge } from "../advisor";
import {
  useNotificationsInfo, useDeliveries, useNotificationDlq, useProvidersHealth,
} from "../../hooks/useNotifications";
import { RelativeTime } from "../automationCenter/shared/Formatters";
import StatusPill from "./shared/StatusPill";
import ChannelIcon from "./shared/ChannelIcon";

// -------------------------------------------------------------------
// 1. DryRunBanner
// -------------------------------------------------------------------

function DryRunBanner() {
  const q = useNotificationsInfo();
  if (q.isLoading || !q.data) return null;
  const dry = q.data.dry_run;
  return (
    <div
      data-testid="notif-dry-run-banner"
      className={`flex items-center justify-between rounded-lg border p-3 text-xs ${
        dry
          ? "border-amber-500/40 bg-amber-500/10 text-amber-500"
          : "border-emerald-500/40 bg-emerald-500/10 text-emerald-500"
      }`}
    >
      <div className="flex items-center gap-2">
        <Bell className="h-4 w-4" />
        <span>
          Dispatcher <strong>{dry ? "DRY-RUN activo" : "ENVÍO REAL activo"}</strong>
          {dry
            ? " · los providers no llaman al API real (fase de rollout §17.6)"
            : " · todos los envíos son reales"}
        </span>
      </div>
      <span className="rounded border border-current px-2 py-0.5 font-mono uppercase tracking-wider">
        {q.data.dispatcher_version}
      </span>
    </div>
  );
}

// -------------------------------------------------------------------
// 2. DispatcherHealthCard
// -------------------------------------------------------------------

function DispatcherHealthCard() {
  const q = useNotificationsInfo();
  const d = q.data;
  return (
    <WidgetCard
      title="Dispatcher"
      description="Salud y catálogo"
      citations={["/api/notifications/info"]}
      testId="widget-dispatcher-health"
    >
      {q.isLoading && <LoadingState />}
      {q.error && <EmptyState message="/info no responde" testId="dispatcher-health-error" />}
      {d && (
        <dl className="grid grid-cols-2 gap-2 text-[10px]" data-testid="dispatcher-health-ok">
          <div>
            <dt className="text-[9px] uppercase tracking-wide text-[var(--ag-text-dim)]">Version</dt>
            <dd className="font-mono">{d.dispatcher_version}</dd>
          </div>
          <div>
            <dt className="text-[9px] uppercase tracking-wide text-[var(--ag-text-dim)]">Templates</dt>
            <dd className="font-mono">{d.templates_count}</dd>
          </div>
          <div>
            <dt className="text-[9px] uppercase tracking-wide text-[var(--ag-text-dim)]">Providers</dt>
            <dd className="font-mono">{(d.providers || []).length}</dd>
          </div>
          <div>
            <dt className="text-[9px] uppercase tracking-wide text-[var(--ag-text-dim)]">AI Freeze</dt>
            <dd className={d.ai_platform_freeze ? "text-emerald-500 font-semibold" : "text-red-500"}>
              {d.ai_platform_freeze ? "OK" : "BROKEN"}
            </dd>
          </div>
        </dl>
      )}
    </WidgetCard>
  );
}

// -------------------------------------------------------------------
// 3. DeliveriesLast24hCard (derived · counts by status)
// -------------------------------------------------------------------

function DeliveriesLast24hCard() {
  const q = useDeliveries({ limit: 500, skip: 0 });
  const stats = useMemo(() => {
    if (!q.data?.items) return null;
    const cutoff = Date.now() - 24 * 3600 * 1000;
    const recent = q.data.items.filter((d) => {
      const t = d.finished_at ? new Date(d.finished_at).getTime() : 0;
      return t >= cutoff;
    });
    const by = {};
    for (const d of recent) {
      by[d.status] = (by[d.status] || 0) + 1;
    }
    return { total: recent.length, by };
  }, [q.data]);
  return (
    <WidgetCard
      title="Últimas 24 h"
      description="Entregas por estado (derivado)"
      citations={["/api/notifications/deliveries?limit=500"]}
      testId="widget-deliveries-24h"
    >
      {q.isLoading && <LoadingState />}
      {q.error && <EmptyState message="No se pudo cargar deliveries" testId="deliveries-24h-error" />}
      {stats && stats.total === 0 && (
        <EmptyState
          title="Sin entregas en las últimas 24 h."
          message="El dispatcher está en dry-run · esperando intents."
          testId="deliveries-24h-empty"
        />
      )}
      {stats && stats.total > 0 && (
        <div className="space-y-2" data-testid="deliveries-24h-ok">
          <div className="text-2xl font-semibold tabular-nums" data-testid="deliveries-24h-total">
            {stats.total} <span className="text-xs text-[var(--ag-text-dim)]">entregas</span>
          </div>
          <div className="flex flex-wrap gap-1">
            {Object.entries(stats.by).map(([s, n]) => (
              <span key={s} className="inline-flex items-center gap-1">
                <StatusPill status={s} showIcon={false} size="xs" />
                <span className="font-mono text-[10px] text-[var(--ag-text-dim)]">{n}</span>
              </span>
            ))}
          </div>
        </div>
      )}
    </WidgetCard>
  );
}

// -------------------------------------------------------------------
// 4. ProvidersMiniStrip
// -------------------------------------------------------------------

function ProvidersMiniStrip() {
  const q = useProvidersHealth();
  return (
    <WidgetCard
      title="Providers"
      description="Salud de canales"
      citations={["/api/notifications/providers/health"]}
      testId="widget-providers-mini"
    >
      {q.isLoading && <LoadingState />}
      {q.error && <EmptyState message="providers/health no responde" testId="providers-mini-error" />}
      {q.data && (
        <div className="grid grid-cols-2 gap-1.5 sm:grid-cols-3" data-testid="providers-mini-ok">
          {Object.entries(q.data.providers || {}).map(([channel, info]) => (
            <div
              key={channel}
              data-testid={`provider-mini-${channel}`}
              className="flex items-center gap-1.5 rounded border border-[var(--ag-border)] bg-[var(--ag-surface-2)]/40 px-2 py-1"
            >
              <span
                className={`h-1.5 w-1.5 rounded-full ${
                  info?.ok ? "bg-emerald-500" : "bg-red-500"
                }`}
              />
              <ChannelIcon channel={channel} size={10} />
              <span className="text-[10px] font-mono uppercase">{channel}</span>
            </div>
          ))}
        </div>
      )}
    </WidgetCard>
  );
}

// -------------------------------------------------------------------
// 5. DlqAlertBanner
// -------------------------------------------------------------------

function DlqAlertBanner({ onGoToDlq }) {
  const q = useNotificationDlq({ limit: 5 });
  const total = q.data?.total || 0;
  if (q.isLoading || !q.data || total === 0) return null;
  return (
    <div
      data-testid="notif-dlq-alert-banner"
      className="flex items-center justify-between rounded-lg border border-red-500/40 bg-red-500/10 p-3"
    >
      <div className="flex items-center gap-2">
        <AlertOctagon className="h-4 w-4 text-red-500" />
        <span className="text-sm">
          <strong data-testid="notif-dlq-alert-count">{total}</strong> notificaciones en DLQ
          requieren revisión
        </span>
      </div>
      <button
        type="button"
        data-testid="notif-dlq-alert-cta"
        onClick={onGoToDlq}
        className="rounded border border-red-500/60 bg-red-500/20 px-3 py-1 text-xs font-medium text-red-500 hover:bg-red-500/30"
      >
        Ver DLQ →
      </button>
    </div>
  );
}

// -------------------------------------------------------------------
// 6. RateLimitHitsCard (derived)
// -------------------------------------------------------------------

function RateLimitHitsCard() {
  const q = useDeliveries({ status: "rate_limited", limit: 50 });
  const total = q.data?.total || 0;
  return (
    <WidgetCard
      title="Rate limiting"
      description="Entregas bloqueadas por rate limit"
      citations={["/api/notifications/deliveries?status=rate_limited"]}
      testId="widget-ratelimit-hits"
    >
      {q.isLoading && <LoadingState />}
      {q.error && <EmptyState testId="ratelimit-hits-error" />}
      {q.data && (
        <div className="space-y-1" data-testid="ratelimit-hits-ok">
          <div className="text-2xl font-semibold tabular-nums">
            {total} <Gauge className="inline h-4 w-4 text-amber-500" />
          </div>
          <div className="text-[10px] text-[var(--ag-text-dim)]">
            provider · recipient · tenant · topic (4-D)
          </div>
        </div>
      )}
    </WidgetCard>
  );
}

// -------------------------------------------------------------------
// 7. ThroughputStrip (KPI cards)
// -------------------------------------------------------------------

function ThroughputStrip() {
  const q = useDeliveries({ limit: 500 });
  const s = useMemo(() => {
    if (!q.data?.items) return null;
    const by = {};
    for (const d of q.data.items) {
      by[d.status] = (by[d.status] || 0) + 1;
    }
    return by;
  }, [q.data]);
  if (!s) return null;
  return (
    <div data-testid="notif-throughput-strip">
      <WidgetGrid cols="md:grid-cols-4 lg:grid-cols-4">
        <MetricCard label="sent" value={s.sent || 0} testId="notif-throughput-sent" hint="ok" />
        <MetricCard label="failed" value={s.failed || 0} testId="notif-throughput-failed" hint="con error" />
        <MetricCard
          label="skipped"
          value={(s.skipped_by_preference || 0) + (s.skipped_no_provider || 0)}
          testId="notif-throughput-skipped"
          hint="preferences / noop"
        />
        <MetricCard label="rate-limited" value={s.rate_limited || 0} testId="notif-throughput-rl" hint="4-D bucket" />
      </WidgetGrid>
    </div>
  );
}

// -------------------------------------------------------------------
// 8. LastSweepIndicator
// -------------------------------------------------------------------

function LastSweepIndicator() {
  const q = useDeliveries({ limit: 1 });
  const last = q.data?.items?.[0];
  return (
    <WidgetCard
      title="Último sweep"
      description="Basado en el último delivery persistido"
      citations={["/api/notifications/deliveries?limit=1"]}
      testId="notif-last-sweep"
    >
      {q.isLoading && <LoadingState />}
      {q.data && !last && (
        <EmptyState
          title="Sin sweeps aún."
          message="Espera 30 s al primer barrido."
          testId="notif-last-sweep-empty"
        />
      )}
      {last && (
        <div className="space-y-1" data-testid="notif-last-sweep-ok">
          <div className="text-sm">
            <RelativeTime iso={last.finished_at} />
          </div>
          <div className="flex items-center gap-2 text-[10px] text-[var(--ag-text-dim)]">
            <ChannelIcon channel={last.channel} size={10} />
            <span className="font-mono">{last.template_id}</span>
          </div>
        </div>
      )}
    </WidgetCard>
  );
}

// -------------------------------------------------------------------
// Compose
// -------------------------------------------------------------------

export default function OverviewTab({ onNavigate }) {
  return (
    <section className="space-y-4" data-testid="notif-overview-tab">
      <DryRunBanner />
      <DlqAlertBanner onGoToDlq={() => onNavigate?.("dlq")} />

      <WidgetGrid cols="lg:grid-cols-3">
        <DispatcherHealthCard />
        <DeliveriesLast24hCard />
        <ProvidersMiniStrip />
      </WidgetGrid>

      <ThroughputStrip />

      <WidgetGrid cols="md:grid-cols-2 lg:grid-cols-2">
        <RateLimitHitsCard />
        <LastSweepIndicator />
      </WidgetGrid>

      <footer className="border-t border-[var(--ag-border)] pt-3">
        <CitationBadge
          sources={["T-17 backend · 19 endpoints · frozen", "AI Platform Freeze · 100%"]}
        />
      </footer>
    </section>
  );
}
